import { Component, computed, effect, inject, input } from '@angular/core';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { Option, DRIE_SCHOOLJAREN_EXCL_2019, DRIE_SCHOOLJAREN_EXCL_2020, isPeiljaarMetExclusief } from '@cumlaude/shared-utils';
import { FilterService } from '../services/filter.service';
import { QueryParamStateService } from '../services/query-param-state.service';
import { INDICATOR_OVER_JAREN } from '../services/weergave-opties';
import { delay } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { FilterName } from '../services/filter-name';

export const indicatorOverOpties: { [key: number]: string } = {
	1: 'Over 1 schooljaar',
	2: 'Over 2 schooljaren',
	3: 'Over 3 schooljaren',
	3.1: 'Over 3 schooljaren excl. coronajaren',
	3.2: 'Over 3 schooljaren excl. coronajaren',
};

/**
 * Component die het aantal schooljaren voor een indicator aanstuurt en de queryparameter synchroniseert.
 * En stelt eventueel de queryparameter bij als een ongeldige waarde is geselecteerd.
 *
 * De `aantalJaren` signal wordt bijgewerkt op basis van het schooljaarfilter en de coronajaar-exclusiveriteit.
 * Uitgangspunt: als de huidige keuze de defaultkeuze is (gegeven het schooljaar), dan wordt de queryparameter gewist.
 * - 3.2 (excl 2019/2020) is nooit de default, want deze moet in de queryparameter staan omdat het schooljaarfilter deze nodig heeft om te filteren.
 * - 3.1 (excl 2020/2021 en 2021/2022 (examencijfers)) is (mits zichtbaar) altijd de default, dus die zou je nooit als queryparameter mogen zien.
 * - 3 is de default als een schooljaar is gekozen waarvoor het coronajaar niet relevant is.
 * - 1 en 2 zijn nooit de default.
 * Als de defaultkeuze actief is en de gebruiker switcht naar een ander schooljaar waardoor het coronajaar wel of niet relevant wordt,
 * dan wordt automatisch geswitcht naar wel en niet excl. coronajaar.
 * NB als de queryparameter leeg is, dan krijgen we hier altijd 3.1 (excl. 2020) binnen.
 */
@Component({
	selector: 'app-schooljaar-indicator-over',
	templateUrl: './schooljaar-indicator-over.component.html',
	styleUrls: ['./schooljaar-indicator-over.component.scss'],
	imports: [SingleSelectDropdownComponent],
})
export class SchooljaarIndicatorOverComponent {
	private readonly filterService = inject(FilterService);
	private readonly qp = inject(QueryParamStateService);

	readonly hideExclusief = input(DRIE_SCHOOLJAREN_EXCL_2019);

	readonly schooljaarFilterName = input<FilterName>('x_onderwijsresultaten_ds_schooljaar');

	jaarOptions = [...INDICATOR_OVER_JAREN, DRIE_SCHOOLJAREN_EXCL_2020, DRIE_SCHOOLJAREN_EXCL_2019].map(
		(value) => new Option<number>(value, indicatorOverOpties[value])
	);

	// Wacht tot een eventuele switch van equivalent schooljaar-filter heeft plaatsgevonden
	readonly schooljaarFilter = toSignal(
		toObservable(this.schooljaarFilterName).pipe(
			switchMap((filterName) => this.filterService.observe(filterName)),
			delay(0)
		)
	);

	readonly metExclusief = computed(() => {
		const filter = this.schooljaarFilter();
		if (!filter) return false;

		const [schooljaar] = filter;
		return isPeiljaarMetExclusief(schooljaar);
	});

	readonly aantalJaren = computed(() => {
		const filter = this.schooljaarFilter();
		if (!filter) return undefined;

		const [_schooljaar, aantalJaren] = filter;
		return this.metExclusief() || aantalJaren <= 3 ? aantalJaren : 3;
	});

	constructor() {
		effect(() => {
			const filter = this.schooljaarFilter();
			if (!filter) return;

			const [_schooljaar, aantalJaren] = filter;

			const hideExclusief = this.hideExclusief();
			const metExclusief = this.metExclusief();
			if (metExclusief && aantalJaren === hideExclusief && aantalJaren === DRIE_SCHOOLJAREN_EXCL_2020) {
				// Onderwijspositie-dashboard: switch naar juiste coronajaar
				this.qp.dispatch('indicator-over', DRIE_SCHOOLJAREN_EXCL_2019, { replaceUrl: true });
			} else if (
				(!metExclusief && aantalJaren === DRIE_SCHOOLJAREN_EXCL_2019) ||
				(metExclusief && aantalJaren === hideExclusief && aantalJaren === DRIE_SCHOOLJAREN_EXCL_2019)
			) {
				// De default is gekozen: verwijder de queryparameter
				this.qp.dispatch('indicator-over', undefined, { replaceUrl: true });
			}
		});
	}

	readonly schooljaarOptions = computed(() => {
		return this.jaarOptions
			.filter((value) => this.metExclusief() || ![DRIE_SCHOOLJAREN_EXCL_2019, DRIE_SCHOOLJAREN_EXCL_2020].includes(value.value))
			.filter((value) => value.value !== this.hideExclusief());
	});

	readonly selectedJaarOption = computed(() => {
		const value = this.aantalJaren();
		if (value === undefined) return undefined;

		return this.jaarOptions.find(({ value: optionValue }) => optionValue === value)!;
	});

	selectJaarOption(optie: number) {
		// Als de default is gekozen: verwijder de queryparameter
		if ((!this.metExclusief() && optie === 3) || (this.metExclusief() && optie > 3 && optie !== this.hideExclusief())) {
			this.qp.dispatch('indicator-over', undefined);
		} else this.qp.dispatch('indicator-over', optie);
	}
}
